// ─────────────────────────────────────────────────────────────────────────────
// ARPFlow Campaign Data Engine
// Reads: invoice metrics Excel + active_platforms.json + kam_contacts.json
// Outputs: processed client metrics, WoW deltas, campaign rows
// ─────────────────────────────────────────────────────────────────────────────

import * as XLSX from 'xlsx';

const YT_LINK = "https://youtu.be/4u6R11lTEm4?si=216wm9f6aixVhEk3";

// ── Formatting helpers ────────────────────────────────────────────────────────
export function fmtInr(val) {
  val = Math.abs(parseFloat(val) || 0);
  if (val >= 1e7) return `₹${(val/1e7).toFixed(1).replace('.0','')}Cr`;
  if (val >= 1e5) return `₹${(val/1e5).toFixed(1).replace('.0','')}L`;
  if (val >= 1000) return `₹${Math.round(val/1000)}K`;
  return `₹${Math.round(val)}`;
}

export function fmtPct(today, t7) {
  if (t7 === 0) return null;
  return ((today - t7) / t7 * 100).toFixed(1);
}

export function fmtPctStr(pct) {
  if (pct === null) return 'N/A';
  const n = parseFloat(pct);
  if (n > 0) return `+${Math.round(n)}%`;
  if (n < 0) return `${Math.round(n)}%`;
  return '0%';
}

export function fmtPctDir(pct) {
  if (pct === null) return 'N/A';
  const n = parseFloat(pct);
  if (n > 0) return `up ${Math.round(Math.abs(n))}%`;
  if (n < 0) return `down ${Math.round(Math.abs(n))}%`;
  return 'flat (0%)';
}

// ── Parse Excel file (invoice metrics) ───────────────────────────────────────
export function parseInvoiceMetrics(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { raw: false });

  // Normalise date + numeric fields
  return rows.map(r => {
    const d = new Date(r['Metric Date']);
    return {
      ...r,
      _date: d,
      _dateStr: d.toISOString().split('T')[0],
      'Client ID': parseInt(r['Client ID']),
      'Overdue Count': parseFloat(r['Overdue Count']) || 0,
      'Overdue Value': parseFloat(r['Overdue Value']) || 0,
      'Rejected Count': parseFloat(r['Rejected Count']) || 0,
      'Rejected Value': parseFloat(r['Rejected Value']) || 0,
      'Short GRN Count': parseFloat(r['Short GRN Count']) || 0,
      'Short GRN Net Value': parseFloat(r['Short GRN Net Value']) || 0,
      'Short GRN Discrepancy Value': parseFloat(r['Short GRN Discrepancy Value']) || 0,
      'No GRN 30+d Count': parseFloat(r['No GRN 30+d Count']) || 0,
      'No GRN 30+d Value': parseFloat(r['No GRN 30+d Value']) || 0,
    };
  });
}

// ── Build active platform set from JSON ───────────────────────────────────────
export function buildActiveSet(platformsJson) {
  const activeSet = new Set();
  (platformsJson.result || []).forEach(p => {
    if (p.sync_enabled && p.platform_live) {
      activeSet.add(`${p.client_uid}|${p.platform_name.toLowerCase()}`);
    }
  });
  return activeSet;
}

// ── Aggregate metrics per client for a given date ────────────────────────────
function aggregateForDate(rows, dateStr, activeSet) {
  const filtered = rows.filter(r =>
    r._dateStr === dateStr &&
    activeSet.has(`${r['Client ID']}|${(r['Platform'] || '').toLowerCase()}`)
  );

  const agg = {};
  filtered.forEach(r => {
    const cid = r['Client ID'];
    if (!agg[cid]) {
      agg[cid] = {
        clientId: cid,
        clientName: r['Client Name'],
        overdueCount: 0, overdueValue: 0,
        rejectedCount: 0, rejectedValue: 0,
        shortGrnCount: 0, shortGrnDiscrepancy: 0,
        noGrn30Count: 0, noGrn30Value: 0,
      };
    }
    const a = agg[cid];
    a.overdueCount    += r['Overdue Count'];
    a.overdueValue    += r['Overdue Value'];
    a.rejectedCount   += r['Rejected Count'];
    a.rejectedValue   += r['Rejected Value'];
    a.shortGrnCount   += r['Short GRN Count'];
    a.shortGrnDiscrepancy += r['Short GRN Discrepancy Value'];
    a.noGrn30Count    += r['No GRN 30+d Count'];
    a.noGrn30Value    += r['No GRN 30+d Value'];
  });
  return agg;
}

// ── Detect new platforms (L1: new platform, L2: metrics went 0→positive) ─────
function detectNewPlatforms(rows, todayStr, t7Str, activeSet) {
  const todayPlatforms = new Set(
    rows.filter(r => r._dateStr === todayStr && activeSet.has(`${r['Client ID']}|${r['Platform']?.toLowerCase()}`))
        .map(r => `${r['Client ID']}|${r['Platform']?.toLowerCase()}`)
  );
  const t7Platforms = new Set(
    rows.filter(r => r._dateStr === t7Str && activeSet.has(`${r['Client ID']}|${r['Platform']?.toLowerCase()}`))
        .map(r => `${r['Client ID']}|${r['Platform']?.toLowerCase()}`)
  );
  const newPlatClients = new Set();
  todayPlatforms.forEach(key => {
    if (!t7Platforms.has(key)) newPlatClients.add(parseInt(key.split('|')[0]));
  });
  return newPlatClients;
}

// ── Main: process all data ────────────────────────────────────────────────────
export function processData(rawRows, platformsJson, contactsJson) {
  // Dates: today = max date in file, t7 = today - 7
  const dates = [...new Set(rawRows.map(r => r._dateStr))].sort();
  const todayStr = dates[dates.length - 1];
  const todayDate = new Date(todayStr);
  const t7Date = new Date(todayDate); t7Date.setDate(t7Date.getDate() - 7);
  const t7Str = t7Date.toISOString().split('T')[0];

  const activeSet = buildActiveSet(platformsJson);

  const todayAgg = aggregateForDate(rawRows, todayStr, activeSet);
  const t7Agg    = aggregateForDate(rawRows, t7Str,    activeSet);
  const newPlatClients = detectNewPlatforms(rawRows, todayStr, t7Str, activeSet);

  // Build contact lookup
  const contactLookup = {};
  (contactsJson.contacts || []).forEach(c => {
    contactLookup[c.id] = c;
  });

  // Build merged client metrics
  const allClientIds = new Set([...Object.keys(todayAgg), ...Object.keys(t7Agg)].map(Number));
  const clientMetrics = {};

  allClientIds.forEach(cid => {
    const t = todayAgg[cid] || {};
    const b = t7Agg[cid]    || {};
    const hasT7 = !!t7Agg[cid];

    const wowDisc = (t.shortGrnDiscrepancy || 0) - (b.shortGrnDiscrepancy || 0);

    clientMetrics[cid] = {
      clientId: cid,
      clientName: (t.clientName || b.clientName || ''),
      newPlatform: newPlatClients.has(cid),
      hasT7Data: hasT7,
      today: {
        overdueCount:       t.overdueCount || 0,
        overdueValue:       t.overdueValue || 0,
        rejectedCount:      t.rejectedCount || 0,
        rejectedValue:      t.rejectedValue || 0,
        shortGrnCount:      t.shortGrnCount || 0,
        shortGrnDiscrepancy:t.shortGrnDiscrepancy || 0,
        noGrn30Count:       t.noGrn30Count || 0,
        noGrn30Value:       t.noGrn30Value || 0,
      },
      t7: {
        overdueCount:       b.overdueCount || 0,
        overdueValue:       b.overdueValue || 0,
        rejectedCount:      b.rejectedCount || 0,
        rejectedValue:      b.rejectedValue || 0,
        shortGrnCount:      b.shortGrnCount || 0,
        shortGrnDiscrepancy:b.shortGrnDiscrepancy || 0,
        noGrn30Count:       b.noGrn30Count || 0,
        noGrn30Value:       b.noGrn30Value || 0,
      },
      wow: {
        overdueValue:       hasT7 ? fmtPct(t.overdueValue||0,      b.overdueValue||0)      : null,
        rejectedValue:      hasT7 ? fmtPct(t.rejectedValue||0,     b.rejectedValue||0)     : null,
        shortGrnDiscrepancy:hasT7 ? fmtPct(t.shortGrnDiscrepancy||0,b.shortGrnDiscrepancy||0):null,
        noGrn30Value:       hasT7 ? fmtPct(t.noGrn30Value||0,      b.noGrn30Value||0)      : null,
        discAddedThisWeek:  wowDisc > 0 ? wowDisc : 0,
      },
    };
  });

  return { clientMetrics, contactLookup, todayStr, t7Str, newPlatClients };
}

// ── Message builders ──────────────────────────────────────────────────────────
function finMessage(firstName, m) {
  const { overdueValue: ov, overdueCount: oc, rejectedValue: rv, rejectedCount: rc, noGrn30Value: ng30v, noGrn30Count: ng30c } = m.today;

  let opening;
  if (ov >= 300000) opening = `⚠️ ${fmtInr(ov)} overdue value can be followed up directly using ARPFlow from customers this week.`;
  else if (rv > 0 && rc > 0) opening = `⚠️ ${fmtInr(rv)} in Rejected Invoices across ${rc} invoices need your attention this week.`;
  else opening = `⚠️ Here is a summary of open items that need your action on ARPFlow this week.`;

  const bullets = [];
  if (ov > 0 && oc > 0)
    bullets.push(`<li>Follow up on <b>${fmtInr(ov)}</b> overdue across <b>${oc} invoice${oc!==1?'s':''}</b> — no need to download anything, just select the invoices and hit Draft Email on the tool. Done in minutes.</li>`);
  if (rv > 0 && rc > 0)
    bullets.push(`<li>Resolve <b>${fmtInr(rv)}</b> in Rejected Invoices across <b>${rc} invoice${rc!==1?'s':''}</b> — go to the Rejected Invoices section to close these cases.</li>`);
  if (ng30v > 0 && ng30c > 0)
    bullets.push(`<li>Request delivery status for <b>${ng30c} invoice${ng30c!==1?'s':''}</b> worth <b>${fmtInr(ng30v)}</b> from your Ops team — these have not been GRNed for more than 30 days; Ops need to confirm or dispute.</li>`);
  if (!bullets.length)
    bullets.push(`<li>All key metrics look healthy this week — keep tracking on ARPFlow.</li>`);

  return `<p>${opening}</p>\n<p>Hi ${firstName}, quick summary of actions you can close directly on ARPFlow &amp; improve your cashflow —</p>\n<ul>\n${bullets.join('\n')}\n</ul>\n<p>Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.</p>`;
}

function opsMessage(firstName, m) {
  const { noGrn30Value: ng30v, noGrn30Count: ng30c } = m.today;
  const wowDisc = m.wow.discAddedThisWeek;
  const sg_wow = wowDisc > 0 ? wowDisc : 0;

  let opening;
  if (sg_wow > 0) opening = `⚠️ ${fmtInr(sg_wow)} worth discrepancies raised by platforms in the last 7 days — dispute within 15 days for a quicker resolution.`;
  else if (ng30v > 0) opening = `⚠️ ${ng30c} invoices worth ${fmtInr(ng30v)} not GRNed for 30+ days — act now before the dispute window closes.`;
  else opening = `⚠️ Here is a summary of items that need your ops action on ARPFlow this week.`;

  const bullets = [];
  if (sg_wow > 0)
    bullets.push(`<li><b>Short GRN:</b> ${fmtInr(sg_wow)} in new discrepancies (last 7 days)<br>ARPFlow → Actions → GRN Short → select invoice → mark Credit Note/upload proof → submit.</li>`);
  if (ng30v > 0 && ng30c > 0)
    bullets.push(`<li><b>${ng30c} invoice${ng30c!==1?'s':''}</b> worth <b>${fmtInr(ng30v)}</b> not GRNed since 30+ days<br>ARPFlow → Actions → GRN Missing → upload stamped POD or enter GRN reference or mark credit note → submit.</li>`);
  if (!bullets.length)
    bullets.push(`<li>All key metrics look healthy this week — keep tracking on ARPFlow.</li>`);

  return `<p>${opening}</p>\n<p>Hi ${firstName}, here's what needs your attention on ARPFlow this week —</p>\n<ul>\n${bullets.join('\n')}\n</ul>\n<p>Brands that dispute within the first 10 days recover significantly more than those that act after 20 days — don't let these age.</p>\n<p>Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.</p>`;
}

function founderMessage(firstName, m) {
  const { overdueValue: ov, overdueCount: oc, rejectedValue: rv, rejectedCount: rc, noGrn30Value: ng30v, noGrn30Count: ng30c } = m.today;
  const wowNoGrn = m.wow.noGrn30Value;
  const wowOv = m.wow.overdueValue;
  const wowRej = m.wow.rejectedValue;

  let opening;
  if (rv > 0 && wowRej !== null && parseFloat(wowRej) > 0)
    opening = `⚠️ ${fmtInr(rv)} in Rejected Invoices this week — your finance team needs to act on these.`;
  else if (wowNoGrn !== null && parseFloat(wowNoGrn) > 0)
    opening = `⚠️ No GRN 30+ days is up ${Math.round(parseFloat(wowNoGrn))}% this week — your team needs to act before these age further.`;
  else if (wowOv !== null && parseFloat(wowOv) > 0)
    opening = `⚠️ Overdue payments are up ${Math.round(parseFloat(wowOv))}% this week — follow up now to protect your cashflow.`;
  else if (wowOv !== null && parseFloat(wowOv) < 0)
    opening = `✅ Overdue payments are down ${Math.round(Math.abs(parseFloat(wowOv)))}% this week — your team is making progress.`;
  else
    opening = `⚠️ Here is your weekly ARPFlow summary to track and act on open items.`;

  const bullets = [];
  if (ov > 0 && oc > 0)
    bullets.push(`<li><b>${fmtInr(ov)}</b> overdue across <b>${oc} invoices</b> — finance team can follow up directly via ARPFlow Draft Email.</li>`);
  if (rv > 0 && rc > 0)
    bullets.push(`<li>Resolve <b>${fmtInr(rv)}</b> in Rejected Invoices across <b>${rc} invoice${rc!==1?'s':''}</b> — finance team can action these directly in the Rejected Invoices section on ARPFlow.</li>`);
  if (ng30v > 0 && ng30c > 0)
    bullets.push(`<li><b>${ng30c} invoices</b> worth <b>${fmtInr(ng30v)}</b> have no GRN for 30+ days — ops team needs to confirm delivery or raise dispute.</li>`);
  if (!bullets.length)
    bullets.push(`<li>All key metrics look healthy this week — keep tracking on ARPFlow.</li>`);

  return `<p>${opening}</p>\n<p>Hi ${firstName}, here's your weekly snapshot —</p>\n<ul>\n${bullets.join('\n')}\n</ul>\n<p>Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.</p>`;
}

// ── Build contact lookup from API responses ───────────────────────────────────
export function buildContactLookupFromApi(usersData, clientsData) {
  const clientKamMap  = {};
  const clientNameMap = {};
  clientsData.forEach(c => {
    clientNameMap[c.client_id] = c.client_name;
    clientKamMap[c.client_id]  = c.kams.length > 0 ? c.kams[0].first_name : '';
  });

  const contactLookup = {};
  usersData.forEach(u => {
    contactLookup[u.user_id] = {
      id:             u.user_id,
      client_uid:     u.client_id,
      client_name:    clientNameMap[u.client_id] || '',
      first_name:     u.first_name  || '',
      last_name:      u.last_name   || '',
      email:          u.email       || '',
      designation:    u.designation || '',
      contact_number: u.contact_number || '',
      kam:            clientKamMap[u.client_id] || '',
    };
  });
  return contactLookup;
}

// ── Aggregate API metrics array into per-client totals ────────────────────────
function aggregateApiMetrics(metricsArray) {
  const agg = {};
  (metricsArray || []).forEach(r => {
    const cid = parseInt(r.client_id);
    if (!agg[cid]) {
      agg[cid] = {
        clientId: cid, clientName: r.client_name,
        overdueCount: 0, overdueValue: 0,
        rejectedCount: 0, rejectedValue: 0,
        shortGrnCount: 0, shortGrnDiscrepancy: 0,
        noGrn30Count: 0, noGrn30Value: 0,
      };
    }
    const a = agg[cid];
    a.overdueCount        += r.overdue_count               || 0;
    a.overdueValue        += r.overdue_value               || 0;
    a.rejectedCount       += r.rejected_count              || 0;
    a.rejectedValue       += r.rejected_value              || 0;
    a.shortGrnCount       += r.short_grn_count             || 0;
    a.shortGrnDiscrepancy += r.short_grn_discrepancy_value || 0;
    a.noGrn30Count        += r.no_grn_gt30d_count          || 0;
    a.noGrn30Value        += r.no_grn_gt30d_value          || 0;
  });
  return agg;
}

// ── Process API metric responses into clientMetrics + WoW deltas ──────────────
export function processApiData(currentWeekResp, priorWeekResp) {
  const todayStr = currentWeekResp.date;
  const t7Str    = priorWeekResp.date;

  const todayAgg = aggregateApiMetrics(currentWeekResp.metrics);
  const t7Agg    = aggregateApiMetrics(priorWeekResp.metrics);

  const todayPlatforms = new Set((currentWeekResp.metrics || []).map(r => `${parseInt(r.client_id)}|${r.platform.toLowerCase()}`));
  const t7Platforms    = new Set((priorWeekResp.metrics   || []).map(r => `${parseInt(r.client_id)}|${r.platform.toLowerCase()}`));
  const newPlatClients = new Set();
  todayPlatforms.forEach(key => {
    if (!t7Platforms.has(key)) newPlatClients.add(parseInt(key.split('|')[0]));
  });

  const allClientIds = new Set([...Object.keys(todayAgg), ...Object.keys(t7Agg)].map(Number));
  const clientMetrics = {};

  allClientIds.forEach(cid => {
    const t = todayAgg[cid] || {};
    const b = t7Agg[cid]    || {};
    const hasT7   = !!t7Agg[cid];
    const wowDisc = (t.shortGrnDiscrepancy || 0) - (b.shortGrnDiscrepancy || 0);

    clientMetrics[cid] = {
      clientId:    cid,
      clientName:  t.clientName || b.clientName || '',
      newPlatform: newPlatClients.has(cid),
      hasT7Data:   hasT7,
      today: {
        overdueCount:        t.overdueCount        || 0,
        overdueValue:        t.overdueValue        || 0,
        rejectedCount:       t.rejectedCount       || 0,
        rejectedValue:       t.rejectedValue       || 0,
        shortGrnCount:       t.shortGrnCount       || 0,
        shortGrnDiscrepancy: t.shortGrnDiscrepancy || 0,
        noGrn30Count:        t.noGrn30Count        || 0,
        noGrn30Value:        t.noGrn30Value        || 0,
      },
      t7: {
        overdueCount:        b.overdueCount        || 0,
        overdueValue:        b.overdueValue        || 0,
        rejectedCount:       b.rejectedCount       || 0,
        rejectedValue:       b.rejectedValue       || 0,
        shortGrnCount:       b.shortGrnCount       || 0,
        shortGrnDiscrepancy: b.shortGrnDiscrepancy || 0,
        noGrn30Count:        b.noGrn30Count        || 0,
        noGrn30Value:        b.noGrn30Value        || 0,
      },
      wow: {
        overdueValue:        hasT7 ? fmtPct(t.overdueValue        || 0, b.overdueValue        || 0) : null,
        rejectedValue:       hasT7 ? fmtPct(t.rejectedValue       || 0, b.rejectedValue       || 0) : null,
        shortGrnDiscrepancy: hasT7 ? fmtPct(t.shortGrnDiscrepancy || 0, b.shortGrnDiscrepancy || 0) : null,
        noGrn30Value:        hasT7 ? fmtPct(t.noGrn30Value        || 0, b.noGrn30Value        || 0) : null,
        discAddedThisWeek:   wowDisc > 0 ? wowDisc : 0,
      },
    };
  });

  return { clientMetrics, todayStr, t7Str, newPlatClients };
}

// ── Campaign row builders ─────────────────────────────────────────────────────
const FOUNDER_DESIGS = new Set(['FOUNDER_CEO','FOUNDER_CFO','FOUNDER_COO']);
const FIN_DESIGS     = new Set(['FINANCE_CONTROLLER','FINANCE_EXECUTIVE']);
const OPS_DESIGS     = new Set(['OPS_EXECUTIVE','OPERATIONS_HEAD']);

function buildCampaignRows(contacts, clientMetrics, newPlatClients, msgFn, eligibleDesigs, excludeNewPlatform = false, onlyNewPlatform = false) {
  const rows = [];
  contacts.forEach(u => {
    if (!eligibleDesigs.has(u.designation)) return;
    const cid = parseInt(u.client_uid);
    const m = clientMetrics[cid];
    if (!m) return;
    if (excludeNewPlatform && newPlatClients.has(cid)) return;
    if (onlyNewPlatform && !newPlatClients.has(cid)) return;

    const wowDisc = m.wow.discAddedThisWeek || 0;
    const showWoW = m.hasT7Data && !onlyNewPlatform;

    rows.push({
      contact_number:             u.contact_number || '',
      first_name:                 u.first_name || '',
      kam_name:                   u.kam || '',
      Message:                    msgFn(u.first_name, m),
      // Individual KPI columns
      overdue_value:              m.today.overdueValue > 0 ? fmtInr(m.today.overdueValue) : 'Nil',
      overdue_count:              m.today.overdueCount,
      overdue_wow:                showWoW ? fmtPctStr(m.wow.overdueValue) : 'N/A',
      overdue_direction:          showWoW ? fmtPctDir(m.wow.overdueValue) : 'N/A',
      rejected_value:             m.today.rejectedValue > 0 ? fmtInr(m.today.rejectedValue) : 'Nil',
      rejected_count:             m.today.rejectedCount,
      rejected_wow:               showWoW ? fmtPctStr(m.wow.rejectedValue) : 'N/A',
      nogrn30_value:              m.today.noGrn30Value > 0 ? fmtInr(m.today.noGrn30Value) : 'Nil',
      nogrn30_count:              m.today.noGrn30Count,
      nogrn30_wow:                showWoW ? fmtPctStr(m.wow.noGrn30Value) : 'N/A',
      nogrn30_direction:          showWoW ? fmtPctDir(m.wow.noGrn30Value) : 'N/A',
      short_grn_discrepancy:      m.today.shortGrnDiscrepancy > 0 ? fmtInr(m.today.shortGrnDiscrepancy) : 'Nil',
      short_grn_count:            m.today.shortGrnCount,
      new_discrepancy_this_week:  wowDisc > 0 ? fmtInr(wowDisc) : 'Nil',
      short_grn_wow:              showWoW ? fmtPctStr(m.wow.shortGrnDiscrepancy) : 'N/A',
      // ref
      client_name:                m.clientName,
      designation:                u.designation,
      new_platform:               newPlatClients.has(cid) ? 'Yes' : 'No',
      report_date:                new Date().toLocaleDateString('en-IN', {day:'numeric', month:'short'}),
    });
  });
  return rows;
}

function founderSummaryMessage(firstName, m) {
  const { overdueValue: ov, overdueCount: oc, noGrn30Value: ng30v, noGrn30Count: ng30c, shortGrnDiscrepancy: sgd, shortGrnCount: sgc, rejectedValue: rv, rejectedCount: rc } = m.today;

  const bullets = [];
  if (ov > 0 && oc > 0)
    bullets.push(`<li><b>Payment Overdue:</b> ${fmtInr(ov)} across ${oc} invoice${oc!==1?'s':''}</li>`);
  if (ng30v > 0 && ng30c > 0)
    bullets.push(`<li><b>No GRN 30+ Days:</b> ${fmtInr(ng30v)} across ${ng30c} invoice${ng30c!==1?'s':''}</li>`);
  if (sgd > 0 && sgc > 0)
    bullets.push(`<li><b>Short GRN Discrepancy:</b> ${fmtInr(sgd)} across ${sgc} invoice${sgc!==1?'s':''}</li>`);
  if (rv > 0 && rc > 0)
    bullets.push(`<li><b>Rejected Invoices:</b> ${fmtInr(rv)} across ${rc} invoice${rc!==1?'s':''}</li>`);

  const body = bullets.length
    ? `<ul>\n${bullets.join('\n')}\n</ul>`
    : `<p>All metrics look healthy this week — keep tracking on ARPFlow.</p>`;

  return `<p>Hi ${firstName}, here's your ARPFlow snapshot for this week across all active platforms —</p>\n${body}\n<p>Your team can act on all of these directly on ARPFlow. Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.</p>`;
}

export function buildAllCampaigns(clientMetrics, contactLookup, newPlatClients) {
  const contacts = Object.values(contactLookup);
  const ALL_DESIGS = new Set([...FOUNDER_DESIGS, ...FIN_DESIGS, ...OPS_DESIGS]);

  return {
    financeExec:       buildCampaignRows(contacts, clientMetrics, newPlatClients, finMessage,            FIN_DESIGS,  false, false),
    financeExecAll:    buildCampaignRows(contacts, clientMetrics, newPlatClients, finMessage,            ALL_DESIGS,  false, false),
    opsExec:           buildCampaignRows(contacts, clientMetrics, newPlatClients, opsMessage,            OPS_DESIGS,  false, false),
    opsExecAll:        buildCampaignRows(contacts, clientMetrics, newPlatClients, opsMessage,            ALL_DESIGS,  false, false),
    founderWoW:        buildCampaignRows(contacts, clientMetrics, newPlatClients, founderMessage,        FOUNDER_DESIGS, true,  false),
    founderWoWAll:     buildCampaignRows(contacts, clientMetrics, newPlatClients, founderMessage,        ALL_DESIGS,     true,  false),
    founderSummary:    buildCampaignRows(contacts, clientMetrics, newPlatClients, founderSummaryMessage, FOUNDER_DESIGS, false, true),
    founderSummaryAll: buildCampaignRows(contacts, clientMetrics, newPlatClients, founderSummaryMessage, ALL_DESIGS,     false, true),
  };
}

// ── Founder WoW SheetWA export ────────────────────────────────────────────────
function _founderOpeningLine(r) {
  if (r.rejected_value !== 'Nil' && r.rejected_wow !== 'N/A' && r.rejected_wow.startsWith('+'))
    return `⚠️ ${r.rejected_value} in Rejected Invoices this week — your finance team needs to act on these.`;
  if (r.nogrn30_wow !== 'N/A' && r.nogrn30_wow.startsWith('+'))
    return `⚠️ No GRN 30+ days is up ${Math.round(parseFloat(r.nogrn30_wow))}% this week — your team needs to act before these age further.`;
  if (r.overdue_wow !== 'N/A' && r.overdue_wow.startsWith('+'))
    return `⚠️ Overdue payments are up ${Math.round(parseFloat(r.overdue_wow))}% this week — follow up now to protect your cashflow.`;
  if (r.overdue_wow !== 'N/A' && r.overdue_wow.startsWith('-'))
    return `✅ Overdue payments are down ${Math.round(Math.abs(parseFloat(r.overdue_wow)))}% this week — your team is making progress.`;
  return `⚠️ Here is your weekly ARPFlow summary to track and act on open items.`;
}

function _variantKey(r) {
  const parts = [];
  if (r.overdue_count > 0 && r.overdue_value !== 'Nil') parts.push('OV');
  if (r.rejected_count > 0 && r.rejected_value !== 'Nil') parts.push('REJ');
  if (r.nogrn30_count > 0 && r.nogrn30_value !== 'Nil') parts.push('NOGRN');
  return parts.join('+') || 'HEALTHY';
}

const _VARIANT_SHEET_NAMES = {
  'OV+REJ+NOGRN': 'OV+REJ+NOGRN',
  'OV+REJ':       'OV+REJ',
  'OV+NOGRN':     'OV+NOGRN',
  'REJ+NOGRN':    'REJ+NOGRN',
  'OV':           'Overdue only',
  'REJ':          'Rejected only',
  'NOGRN':        'No GRN only',
  'HEALTHY':      'Healthy',
};
const _VARIANT_ORDER = ['OV+REJ+NOGRN','OV+REJ','OV+NOGRN','REJ+NOGRN','OV','REJ','NOGRN','HEALTHY'];

function _variantCfg(vk) {
  const hasOv    = vk !== 'HEALTHY' && vk.includes('OV');
  const hasRej   = vk !== 'HEALTHY' && vk.includes('REJ');
  const hasNoGrn = vk !== 'HEALTHY' && vk.includes('NOGRN');
  const healthy  = vk === 'HEALTHY';
  const sheetName = _VARIANT_SHEET_NAMES[vk] || vk;

  const metricCols = [
    ...(hasOv    ? ['overdue_value',  'overdue_count']  : []),
    ...(hasRej   ? ['rejected_value', 'rejected_count'] : []),
    ...(hasNoGrn ? ['nogrn30_count',  'nogrn30_value']  : []),
  ];
  const headers = [
    'contact_number', 'first_name', 'kam_name', 'opening_line',
    ...metricCols,
    'client_name', 'designation', 'new_platform', 'report_date',
  ];

  const bullets = [
    hasOv    && `• @overdue_value overdue across @overdue_count invoices — finance team can follow up directly via ARPFlow Draft Email.`,
    hasRej   && `• Resolve @rejected_value in Rejected Invoices across @rejected_count invoices — finance team can action these directly in the Rejected Invoices section on ARPFlow.`,
    hasNoGrn && `• @nogrn30_count invoices worth @nogrn30_value have no GRN for 30+ days — ops team needs to confirm delivery or raise dispute.`,
    healthy  && `• All key metrics look healthy this week — keep tracking on ARPFlow.`,
  ].filter(Boolean);

  const template = [
    `@opening_line`,
    ``,
    `Hi @first_name, here's your weekly snapshot —`,
    ``,
    ...bullets,
    ``,
    `Need help? Refer this video ${YT_LINK} or reach out and we'll set up a quick walkthrough call.`,
  ].join('\n');

  const metricWidths = [
    ...(hasOv    ? [14, 12] : []),
    ...(hasRej   ? [14, 12] : []),
    ...(hasNoGrn ? [12, 14] : []),
  ];
  const colWidths = [18, 14, 12, 70, ...metricWidths, 22, 18, 9, 11];

  return { sheetName, headers, template, colWidths, varCols: ['opening_line', ...metricCols] };
}

export function exportFounderWoWExcel(rows, campaignName, kamFilter) {
  const filtered = kamFilter ? rows.filter(r => r.kam_name === kamFilter) : rows;
  const sorted   = [...filtered].sort((a,b) => a.client_name.localeCompare(b.client_name) || a.first_name.localeCompare(b.first_name));

  const enriched = sorted.map(r => ({ ...r, opening_line: _founderOpeningLine(r), _variant: _variantKey(r) }));

  const groups = {};
  enriched.forEach(r => {
    if (!groups[r._variant]) groups[r._variant] = [];
    groups[r._variant].push(r);
  });

  const wb = XLSX.utils.book_new();
  const tmplData = [['Variant Sheet', 'Count', 'Variable Columns', 'Template Message (paste into SheetWA)']];

  _VARIANT_ORDER.forEach(vk => {
    const varRows = groups[vk];
    if (!varRows) return;
    const cfg = _variantCfg(vk);
    const wsData = [cfg.headers, ...varRows.map(r => cfg.headers.map(h => r[h] ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = cfg.colWidths.map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, cfg.sheetName);
    tmplData.push([cfg.sheetName, varRows.length, cfg.varCols.join(', '), cfg.template]);
  });

  const tmplWs = XLSX.utils.aoa_to_sheet(tmplData);
  tmplWs['!cols'] = [{ wch: 18 }, { wch: 8 }, { wch: 50 }, { wch: 100 }];
  XLSX.utils.book_append_sheet(wb, tmplWs, 'Templates');

  const label = kamFilter ? `_${kamFilter}` : '_AllKAMs';
  const date  = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `${campaignName}${label}_${date}.xlsx`);
}

// ── Excel export ──────────────────────────────────────────────────────────────
export function exportCampaignExcel(rows, campaignName, kamFilter) {
  const filtered = kamFilter ? rows.filter(r => r.kam_name === kamFilter) : rows;
  const sorted   = [...filtered].sort((a,b) => a.client_name.localeCompare(b.client_name) || a.first_name.localeCompare(b.first_name));

  const headers = [
    'contact_number','first_name','kam_name','Message',
    'overdue_value','overdue_count','overdue_wow','overdue_direction',
    'rejected_value','rejected_count','rejected_wow',
    'nogrn30_value','nogrn30_count','nogrn30_wow','nogrn30_direction',
    'short_grn_discrepancy','short_grn_count','new_discrepancy_this_week','short_grn_wow',
    'client_name','designation','new_platform','report_date',
  ];

  const wsData = [headers, ...sorted.map(r => headers.map(h => r[h] ?? ''))];
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Column widths
  const widths = [18,14,12,80,14,12,12,16,13,12,12,14,12,12,16,14,12,18,12,22,18,9,11];
  ws['!cols'] = widths.map(w => ({ wch: w }));

  XLSX.utils.book_append_sheet(wb, ws, kamFilter || 'All KAMs');

  const label = kamFilter ? `_${kamFilter}` : '_AllKAMs';
  const date  = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `${campaignName}${label}_${date}.xlsx`);
}
